import React, { useMemo } from 'react';

import { useRecoilValue } from 'recoil';

import { hasModule, inRn, pluralize } from 'lib/helpers/utility';
import { COMPLETED_TRANSACTION_STATUSES } from 'lib/constants/transaction/completedTransactionStatuses';

import { modulesState } from 'components/state';
import Allowed from 'components/shared/Permissions/Allowed';

import { TableFooterSummaryItem } from '../ReceiptTable/components/TableFooterSummary';

import { calculateTotals, calculateWalkoutPaid } from './lib/helpers';

const ReceiptSummary = ({
  transaction,
  transactions,
  isPatient,
  unallocatedAmount = 0,
  walkout = false,
  walkoutTransactions = []
}) => {
  const { id, amount_paid_self, surcharge_amount, type, amount_refunded } = transaction || {};

  const practiceModules = useRecoilValue(modulesState);
  const hasTaxModule = hasModule(practiceModules, 'medipay.tax');
  const hasTechFeeModule = hasModule(practiceModules, 'medipay.technology_fee');

  const isWriteoff = type === 'writeoff';

  const surchargeAmount = inRn(surcharge_amount);
  const amountPaid = inRn(amount_paid_self) - surchargeAmount;
  const amountTotal = inRn(transaction?.amount_total);

  const amountPaidWithSurcharge = amountPaid + surchargeAmount;

  const totals = useMemo(() => calculateTotals(transactions), [transactions]);

  const walkoutPaid = useMemo(
    () => calculateWalkoutPaid(walkoutTransactions),
    [walkoutTransactions]
  );

  const previousPayments = totals.totalAmountPaid - (!isWriteoff ? amountPaid : 0);

  const hasInvoiceOrManyTransactions =
    !!transaction?.invoice || (transactions.length > 0 && transaction?.id !== transactions[0]?.id);

  const amountDue = transactions?.reduce((acc, t) => {
    if (!t.invoice) return acc;

    const { balance = 0 } = t?.invoice || {};

    return acc + balance + (isWriteoff ? amountPaid : 0);
  }, 0);

  const isCompleted = COMPLETED_TRANSACTION_STATUSES.includes(transaction?.status?.toLowerCase());

  return (
    <Allowed
      requiredPermissions={isPatient ? '' : 'payments.read'}
      requiredModules={isPatient ? '' : 'medipay.payment_suite'}>
      <div className="flex w-full max-w-[332px] flex-col items-end gap-4 self-end sm:max-w-full">
        <div className="flex w-full flex-col border-0 border-b border-solid border-b-neutral-900 py-1">
          <TableFooterSummaryItem
            label="Subtotal"
            value={
              !hasInvoiceOrManyTransactions
                ? amountPaid + (amount_refunded || 0)
                : totals.totalAmount
            }
          />
          {(hasTechFeeModule || surchargeAmount > 0) && (
            <TableFooterSummaryItem label="Technology fee" value={surchargeAmount} />
          )}
          {amount_refunded > 0 && (
            <TableFooterSummaryItem label="Refund" value={amount_refunded * -1} />
          )}
          {hasInvoiceOrManyTransactions && (
            <>
              {totals?.totalWriteOff > 0 && !isWriteoff && (
                <TableFooterSummaryItem label="Write off" value={totals.totalWriteOff * -1} />
              )}

              <TableFooterSummaryItem label="Discount" value={totals.totalDiscount * -1} />

              {hasTaxModule && <TableFooterSummaryItem label="Tax" value={totals.totalTax} />}

              {!walkout && previousPayments > 0 && (
                <TableFooterSummaryItem label="Previous payments" value={previousPayments * -1} />
              )}
            </>
          )}
          <TableFooterSummaryItem
            label="Current amount due"
            value={
              hasInvoiceOrManyTransactions
                ? amountDue + amountPaidWithSurcharge
                : totals.totalBalance + amountPaidWithSurcharge
            }
          />
          {id && (
            <TableFooterSummaryItem
              className="h-10"
              label={`Total amount${walkout || isCompleted ? ' paid' : ''}`}
              value={walkout ? walkoutPaid : isCompleted ? amountPaidWithSurcharge : amountTotal}
              color="neutral-900"
            />
          )}
          <div className="border-0 border-b border-solid border-b-neutral-900" />
        </div>
        {id && (
          <div className="flex w-full flex-col rounded-md bg-neutral-50 px-2">
            {totals?.totalBalance > 0 && (
              <TableFooterSummaryItem
                label={`Remaining invoice ${pluralize('balance', transactions?.length)}`}
                value={totals?.totalBalance}
                valueClassName="text-neutral-900"
              />
            )}
            {unallocatedAmount > 0 && isCompleted && (
              <TableFooterSummaryItem
                label="Unallocated amount"
                value={unallocatedAmount}
                valueClassName="text-neutral-900"
              />
            )}
          </div>
        )}
      </div>
    </Allowed>
  );
};

export default ReceiptSummary;
